import { useEffect, useState, useCallback } from "react";
import { Plus, Storefront, Gavel, Trash, ChatCircleDots, Trophy, CaretDown, ShoppingCart, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { CaptionedImage, AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const EMPTY_LISTING = {
  title: "", description: "", category: "", price: 0, currency: "usd",
  quantity: 1, unit: "pcs", image_id: null, image_caption: "",
};
const EMPTY_TENDER = {
  title: "", description: "", quantity: 1, unit: "pcs", currency: "usd", budget: 0, closes_on: "",
};

export default function Marketplace() {
  const { user } = useAuth();
  const { currencies, format } = useCurrency();
  const { home_country: homeCountry } = useCurrency();
  const [dutyHints, setDutyHints] = useState({});
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [bids, setBids] = useState({});
  const [openTender, setOpenTender] = useState(null);
  const [lOpen, setLOpen] = useState(false);
  const [tOpen, setTOpen] = useState(false);
  const [bidFor, setBidFor] = useState(null);
  const [lForm, setLForm] = useState(EMPTY_LISTING);
  const [tForm, setTForm] = useState(EMPTY_TENDER);
  const [bForm, setBForm] = useState({ price: 0, lead_time_days: 7, note: "" });

  const canSell = ["admin", "vendor"].includes(user?.role);
  const canTender = ["admin", "buyer"].includes(user?.role);

  const load = useCallback(async () => {
    const [l, t] = await Promise.all([api.get("/listings"), api.get("/tenders")]);
    setListings(l.data);
    setTenders(t.data);
    if (homeCountry) {
      const foreign = [...new Set(l.data.map((x) => x.country).filter((c) => c && c !== homeCountry))];
      const hints = {};
      await Promise.all(foreign.slice(0, 12).map(async (c) => {
        try {
          const { data } = await api.get(`/tariffs/lookup?dest_country=${homeCountry}&origin_country=${c}&category=other`);
          hints[c] = data.duty_pct;
        } catch { /* no rate on file */ }
      }));
      setDutyHints(hints);
    }
  }, [homeCountry]);

  useEffect(() => {
    load();
  }, [load]);

  const loadBids = async (tender) => {
    if (openTender === tender.id) {
      setOpenTender(null);
      return;
    }
    const { data } = await api.get(`/tenders/${tender.id}/bids`);
    setBids((b) => ({ ...b, [tender.id]: data }));
    setOpenTender(tender.id);
  };

  const postListing = async (e) => {
    e.preventDefault();
    try {
      await api.post("/listings", { ...lForm, price: Number(lForm.price), quantity: Number(lForm.quantity) });
      toast.success("Advert posted to the marketplace");
      setLOpen(false);
      setLForm(EMPTY_LISTING);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const postTender = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenders", {
        ...tForm, quantity: Number(tForm.quantity), budget: Number(tForm.budget),
        closes_on: tForm.closes_on || null,
      });
      toast.success("Tender opened for bidding");
      setTOpen(false);
      setTForm(EMPTY_TENDER);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitBid = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tenders/${bidFor.id}/bids`, {
        price: Number(bForm.price),
        lead_time_days: Number(bForm.lead_time_days),
        note: bForm.note,
      });
      toast.success("Bid submitted");
      setBidFor(null);
      setBForm({ price: 0, lead_time_days: 7, note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const award = async (tender, bid) => {
    try {
      await api.post(`/tenders/${tender.id}/award/${bid.id}`);
      toast.success(`Awarded to ${bid.bidder_name}`);
      const { data } = await api.get(`/tenders/${tender.id}/bids`);
      setBids((b) => ({ ...b, [tender.id]: data }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setListingStatus = async (id, status) => {
    try {
      await api.patch(`/listings/${id}/status`, { status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addToLoad = async (l) => {    try {
      await api.post("/cart/items", { source: "listing", ref_id: l.id, quantity: 1 });
      toast.success(`${l.title} added to your load basket — open Shipping to book it`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const shareAsStory = async (l) => {
    try {
      const { data } = await api.post("/stories", { source: "listing", ref_id: l.id });
      navigator.clipboard?.writeText(`${data.caption}\n\n${(data.hashtags || []).join(" ")}\n${window.location.origin}/s/${data.token}`)?.catch(() => {});
      toast.success("Story card created — caption copied. Open Stories & Reels to post it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeListing = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Advert removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const currencyOptions = Object.entries(currencies || { usd: { symbol: "$", label: "US Dollar" } });

  return (
    <div data-testid="marketplace-page">
      <PageHeader
        overline="Marketplace"
        title="Adverts & Tenders"
        description="Vendors advertise surplus stock, tools and services. Buyers open tenders and award the bid that suits them."
        action={
          <div className="flex flex-wrap gap-2">
            {canSell && (
              <Button
                data-testid="add-listing-button"
                onClick={() => setLOpen(true)}
                variant="outline"
                className="h-11 flex-1 rounded-none border-slate-300 text-xs font-semibold uppercase tracking-widest hover:bg-slate-100 sm:h-10 sm:flex-none"
              >
                <Storefront size={16} className="mr-2" /> Post advert
              </Button>
            )}
            {canTender && (
              <Button
                data-testid="add-tender-button"
                onClick={() => setTOpen(true)}
                className="h-11 flex-1 rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:flex-none"
              >
                <Gavel size={16} className="mr-2" /> Open tender
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard testid="market-stat-adverts" label="Live adverts" value={listings.filter((l) => l.status === "active").length} icon={Storefront} />
        <StatCard testid="market-stat-tenders" label="Open tenders" value={tenders.filter((t) => t.status === "open").length} icon={Gavel} tone="primary" />
        <StatCard testid="market-stat-bids" label="Bids received" value={tenders.reduce((s, t) => s + (t.bid_count || 0), 0)} icon={Gavel} />
        <StatCard testid="market-stat-awarded" label="Awarded" value={tenders.filter((t) => t.status === "awarded").length} icon={Trophy} tone="success" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="market-tab-listings" value="listings" className="rounded-none text-xs uppercase tracking-wider">
            Adverts ({listings.length})
          </TabsTrigger>
          <TabsTrigger data-testid="market-tab-tenders" value="tenders" className="rounded-none text-xs uppercase tracking-wider">
            Tenders ({tenders.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "listings" ? (
        listings.length === 0 ? (
          <EmptyState testid="listings-empty" title="No adverts yet" description={canSell ? "Post surplus stock, a spare tool or a service." : "Vendors and sellers can advertise here."} />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="listings-grid">
            {listings.map((l) => (
              <div key={l.id} data-testid={`listing-card-${l.id}`} className="flex h-full flex-col border border-border bg-white p-4 transition-colors duration-150 hover:border-slate-400">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display truncate text-base font-semibold text-[#0F172A]">{l.title}</h3>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <StatusBadge testid={`listing-status-${l.id}`} value={l.status} />
                      {l.category && <span className="overline text-slate-400">{l.category}</span>}
                    </div>
                  </div>
                  <span data-testid={`listing-price-${l.id}`} className="shrink-0 font-mono text-sm font-bold text-[#002FA7]">
                    {format(l.price, l.currency)}
                  </span>
                </div>

                <CaptionedImage
                  testid={`listing-image-${l.id}`}
                  imageId={l.image_id}
                  caption={l.image_caption}
                  className="h-40 w-full border border-border object-cover"
                />
                {(l.images || []).length > 1 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto" data-testid={`listing-gallery-${l.id}`}>
                    {l.images.slice(1).map((img) => (
                      <AuthImage
                        key={img.image_id}
                        imageId={img.image_id}
                        alt={img.caption || l.title}
                        testid={`listing-extra-${img.image_id}`}
                        className="h-14 w-14 shrink-0 border border-border object-cover"
                      />
                    ))}
                  </div>
                )}

                {l.description && <p className="mt-3 flex-1 text-sm text-slate-600">{l.description}</p>}

                <div className="mt-3 text-xs text-slate-500">
                  {l.quantity} {l.unit} available
                  {l.country ? ` · from ${l.country}` : ""} · {l.seller_name}
                  {l.seller_company ? ` (${l.seller_company})` : ""}
                </div>
                {l.country && homeCountry && l.country !== homeCountry && dutyHints[l.country] > 0 && (
                  <div data-testid={`duty-hint-${l.id}`} className="mt-2 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-2.5 py-1.5 text-xs text-[#0F172A]">
                    Cross-border — plus roughly {dutyHints[l.country]}% import duty into {homeCountry}
                    on general goods, before freight and VAT. <a href="/tariffs" className="font-semibold text-[#002FA7] hover:underline">Work out the landed cost for this product</a>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                  <button
                    data-testid={`share-story-${l.id}`}
                    onClick={() => shareAsStory(l)}
                    className="flex items-center gap-1.5 border border-border px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 hover:bg-slate-100"
                  >
                    <ShareNetwork size={13} /> Story
                  </button>
                  <button
                    data-testid={`add-to-load-${l.id}`}
                    onClick={() => addToLoad(l)}
                    className="flex items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#00227A]"
                  >
                    <ShoppingCart size={13} /> Add to load
                  </button>
                  <a
                    data-testid={`contact-seller-${l.id}`}
                    href="/network"
                    className="flex items-center gap-1.5 border border-border px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-[#002FA7] hover:bg-[#EEF2FF]"
                  >
                    <ChatCircleDots size={13} /> Contact seller
                  </a>
                  {(user?.role === "admin" || l.seller_id === user?.id) && (
                    <>
                      <Select value={l.status} onValueChange={(v) => setListingStatus(l.id, v)}>
                        <SelectTrigger data-testid={`listing-status-select-${l.id}`} className="h-9 w-28 rounded-none border-slate-300 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["active", "sold", "withdrawn"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button
                        data-testid={`delete-listing-${l.id}`}
                        onClick={() => removeListing(l.id)}
                        className="border border-border p-2 text-[#FF3B30] hover:bg-[#FFECEB]"
                      >
                        <Trash size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : tenders.length === 0 ? (
        <EmptyState testid="tenders-empty" title="No tenders open" description={canTender ? "Open a tender and let vendors bid for the work." : "Buyers open tenders here — you'll be able to bid."} />
      ) : (
        <div className="space-y-3" data-testid="tenders-list">
          {tenders.map((t) => {
            const mine = t.buyer_id === user?.id || user?.role === "admin";
            const rows = bids[t.id] || [];
            return (
              <div key={t.id} data-testid={`tender-card-${t.id}`} className="border border-border bg-white">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-base font-semibold text-[#0F172A]">{t.title}</h3>
                      <StatusBadge testid={`tender-status-${t.id}`} value={t.status} />
                    </div>
                    {t.description && <p className="mt-2 text-sm text-slate-600">{t.description}</p>}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>{t.quantity} {t.unit}</span>
                      {t.budget > 0 && <span>Budget {format(t.budget, t.currency)}</span>}
                      {t.closes_on && <span>Closes {t.closes_on}</span>}
                      <span data-testid={`tender-bidcount-${t.id}`}>{t.bid_count || 0} bid(s)</span>
                      {t.best_bid != null && <span className="font-semibold text-[#0B7A5B]">Best {format(t.best_bid, t.currency)}</span>}
                      {t.awarded_to && <span className="font-semibold text-[#0B7A5B]">Awarded to {t.awarded_to}</span>}
                      <span>by {t.buyer_name}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {canSell && t.status === "open" && (
                      <button
                        data-testid={`bid-tender-${t.id}`}
                        onClick={() => setBidFor(t)}
                        className="flex h-10 items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] px-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#00227A]"
                      >
                        <Gavel size={14} /> Place bid
                      </button>
                    )}
                    <button
                      data-testid={`toggle-bids-${t.id}`}
                      onClick={() => loadBids(t)}
                      className="flex h-10 items-center gap-1.5 border border-border px-3 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50"
                    >
                      <CaretDown size={13} className={openTender === t.id ? "rotate-180" : ""} />
                      {mine ? "Bids" : "My bid"}
                    </button>
                  </div>
                </div>

                {openTender === t.id && (
                  <div className="border-t border-border bg-slate-50 p-4" data-testid={`bids-panel-${t.id}`}>
                    {rows.length === 0 ? (
                      <p className="text-xs text-slate-500">No bids yet.</p>
                    ) : (
                      <ul className="divide-y divide-border border border-border bg-white">
                        {rows.map((b) => (
                          <li key={b.id} data-testid={`bid-${b.id}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                            <div className="min-w-0">
                              <span className="text-sm font-semibold text-[#0F172A]">
                                {format(b.price, t.currency)}
                              </span>
                              <span className="ml-2 text-xs text-slate-500">
                                {b.lead_time_days} days · {b.bidder_name}
                                {b.bidder_company ? ` (${b.bidder_company})` : ""}
                              </span>
                              {b.note && <span className="block text-xs italic text-slate-400">{b.note}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <StatusBadge testid={`bid-status-${b.id}`} value={b.status} />
                              {mine && t.status === "open" && (
                                <button
                                  data-testid={`award-bid-${b.id}`}
                                  onClick={() => award(t, b)}
                                  className="flex items-center gap-1.5 border border-border px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#0B7A5B] hover:bg-[#E6F7F1]"
                                >
                                  <Trophy size={13} /> Award
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Post advert */}
      <Dialog open={lOpen} onOpenChange={setLOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Post an advert</DialogTitle>
            <DialogDescription>Surplus stock, a spare tool or a service you offer.</DialogDescription>
          </DialogHeader>
          <form onSubmit={postListing} className="space-y-4" data-testid="add-listing-form">
            <div>
              <Label className="overline text-slate-500">Title</Label>
              <Input data-testid="listing-title-input" value={lForm.title} onChange={(e) => setLForm({ ...lForm, title: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">Description</Label>
              <Textarea data-testid="listing-description-input" rows={3} value={lForm.description} onChange={(e) => setLForm({ ...lForm, description: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Category</Label>
                <Input data-testid="listing-category-input" value={lForm.category} onChange={(e) => setLForm({ ...lForm, category: e.target.value })} placeholder="surplus stock, tool, service" className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Currency</Label>
                <Select value={lForm.currency} onValueChange={(v) => setLForm({ ...lForm, currency: v })}>
                  <SelectTrigger data-testid="listing-currency-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map(([code, meta]) => (
                      <SelectItem key={code} value={code}>{meta.symbol} {code.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Price</Label>
                <Input data-testid="listing-price-input" type="number" min="0" step="0.01" value={lForm.price} onChange={(e) => setLForm({ ...lForm, price: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Quantity</Label>
                <Input data-testid="listing-quantity-input" type="number" min="0" step="any" value={lForm.quantity} onChange={(e) => setLForm({ ...lForm, quantity: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit</Label>
                <Input data-testid="listing-unit-input" value={lForm.unit} onChange={(e) => setLForm({ ...lForm, unit: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <ImageCaptionField
              testid="listing-image"
              value={lForm.image_id}
              caption={lForm.image_caption}
              onChange={({ image_id, image_caption }) => setLForm((f) => ({ ...f, image_id, image_caption }))}
            />
            <DialogFooter>
              <Button data-testid="save-listing-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Post advert
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Open tender */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Open a tender</DialogTitle>
            <DialogDescription>Vendors submit sealed bids; you award the one you want.</DialogDescription>
          </DialogHeader>
          <form onSubmit={postTender} className="space-y-4" data-testid="add-tender-form">
            <div>
              <Label className="overline text-slate-500">What do you need?</Label>
              <Input data-testid="tender-title-input" value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">Specification</Label>
              <Textarea data-testid="tender-description-input" rows={3} value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Quantity</Label>
                <Input data-testid="tender-quantity-input" type="number" min="0" step="any" value={tForm.quantity} onChange={(e) => setTForm({ ...tForm, quantity: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit</Label>
                <Input data-testid="tender-unit-input" value={tForm.unit} onChange={(e) => setTForm({ ...tForm, unit: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Currency</Label>
                <Select value={tForm.currency} onValueChange={(v) => setTForm({ ...tForm, currency: v })}>
                  <SelectTrigger data-testid="tender-currency-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map(([code, meta]) => (
                      <SelectItem key={code} value={code}>{meta.symbol} {code.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Budget</Label>
                <Input data-testid="tender-budget-input" type="number" min="0" step="0.01" value={tForm.budget} onChange={(e) => setTForm({ ...tForm, budget: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Closes on</Label>
                <Input data-testid="tender-closes-input" type="date" value={tForm.closes_on} onChange={(e) => setTForm({ ...tForm, closes_on: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-tender-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Open tender
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Place bid */}
      <Dialog open={!!bidFor} onOpenChange={(v) => !v && setBidFor(null)}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Place your bid</DialogTitle>
            <DialogDescription>{bidFor?.title}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBid} className="space-y-4" data-testid="add-bid-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Your price ({(bidFor?.currency || "usd").toUpperCase()})</Label>
                <Input data-testid="bid-price-input" type="number" min="0" step="0.01" value={bForm.price} onChange={(e) => setBForm({ ...bForm, price: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Lead time (days)</Label>
                <Input data-testid="bid-lead-input" type="number" min="0" value={bForm.lead_time_days} onChange={(e) => setBForm({ ...bForm, lead_time_days: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Note</Label>
              <Textarea data-testid="bid-note-input" rows={3} value={bForm.note} onChange={(e) => setBForm({ ...bForm, note: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <DialogFooter>
              <Button data-testid="save-bid-button" type="submit" className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
                Submit bid
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
